'use client';

import clsx from 'clsx';
import { randomUUID } from 'crypto';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormGetValues, UseFormWatch } from 'react-hook-form';

import { BuyTicketFormValues } from '@/components/Form/MultiStepForm/BuyTicketForm.type';
import { useMultiStepFormState } from '@/components/Form/MultiStepForm/MultiStepFormContext';
import NumberField from '@/components/Form/UI/NumberField';
import PromoCodeModal from '@/components/Modal/PromoCodeModal';
import RegisterModal from '@/components/Modal/RegisterModal';
import Button from '@/components/UI/Button';
import Disclosure from '@/components/UI/Disclosure';
import RichText from '@/components/UI/RichText';
import DiscountSVG from '@/public/page/order/discount.svg';
import { DiscountEventTicketType } from '@/types/discountEventTicketType';
import { Event } from '@/types/event';
import { EventTicketType } from '@/types/eventTicketType';
import { Format } from '@/utils/format';
import getPriceWithCurrency from '@/utils/getPriceWithCurrency';
import { DiscountEventTicketTypeHelper } from '@/utils/helpers/DiscountEventTicketTypeHelper';
import { EventHelper } from '@/utils/helpers/EventHelper';
import { EventTicketTypeHelper } from '@/utils/helpers/EventTicketTypeHelper';
import { useCurrentLocale } from '@/utils/locale/localeClient';
import { generateUUID } from '@/utils/uuid';

import EventPageNotPublished from './EventPageNotPublished';

interface EventPageSelectTicketSectionProps {
  event: Event;
  placeId: string;
  coverUrl?: string;
  userToken?: string;
  eventTicketTypes: EventTicketType[];
}

export default function EventPageSelectTicketSection({
  event,
  placeId,
  coverUrl,
  userToken,
  eventTicketTypes,
}: EventPageSelectTicketSectionProps) {
  // States
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);
  const [showLoginCodeModal, setShowLoginCodeModal] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  // Hooks
  const t = useTranslations('Pages.Event.EventPageSelectTicketSection');
  const locale = useCurrentLocale();

  // Refs
  const buyTicketRef = useRef<HTMLDivElement>(null);

  // Form
  const { updateFormData, formData } =
    useMultiStepFormState<BuyTicketFormValues>();
  const { watch, setValue, getValues } = useForm<BuyTicketFormValues>({
    defaultValues: {
      ticketCount: 0,
      entryType: event.entryType,
      eventName: event.name,
      eventId: event.id,
      placeId,
      coverUrl,
      event: event,
      eventTicketType: eventTicketTypes,
      eventTicketTypes: {},
    },
  });
  useEffect(() => {
    if (event) {
      setIsPublished(EventHelper.isPublished(event));
    }
  }, [event]);

  const isEventExpired = EventHelper.isExpired(event);
  const isSoldOut = EventHelper.isSoldOut(eventTicketTypes);
  const hasEventTicketTypes = eventTicketTypes && eventTicketTypes.length > 0;

  if (!isPublished)
    return (
      <EventPageNotPublished
        publishedAt={event.publishedAt}
        setIsPublished={setIsPublished}
      />
    );

  if (isEventExpired) {
    return (
      <p className='text-center text-xl font-bold text-primary-filled-default'>
        {t('finished')}
      </p>
    );
  }

  if (isSoldOut) {
    return (
      <p className='text-center text-xl font-bold text-primary-filled-default'>
        {t('soldout')}
      </p>
    );
  }

  if (!hasEventTicketTypes) {
    return (
      <p className='text-center text-xl font-bold text-primary-filled-default'>
        {t('noTickets')}
      </p>
    );
  }

  return (
    <>
      <form className='pt-6 text-white-filled-default'>
        <div className='h-[1px] w-full bg-neutral-faded-default'></div>
        <div className='mb-16' ref={buyTicketRef}>
          <h3 className='my-6 text-xl font-extrabold'>{t('tickets')}</h3>
          <div className='mb-3'>
            <Button
              type='button'
              className='relative z-10 flex items-center space-x-1'
              onClick={() =>
                userToken
                  ? setShowPromoCodeModal(true)
                  : setShowLoginCodeModal(true)
              }
            >
              <div className='flex items-center space-x-2'>
                <p>{t('promoCodeAdd')}</p>
                <DiscountSVG style={{ color: 'white' }} />
              </div>
            </Button>
          </div>
          {event.entryType === 'private' && (
            <p className='mb-6 font-medium'>{t('privateEvent')}</p>
          )}

          <div className='space-y-6'>
            {eventTicketTypes.map((eventTicketType, index) => (
              <EventTicketSelector
                updateFormData={updateFormData}
                key={eventTicketType.id}
                eventTicketType={eventTicketType}
                getValues={getValues}
                setValue={setValue}
                event={event}
                watch={watch}
              />
            ))}
          </div>
        </div>
      </form>

      {showPromoCodeModal && (
        <PromoCodeModal
          open={showPromoCodeModal}
          setOpen={setShowPromoCodeModal}
          placeId={placeId}
        />
      )}

      {showLoginCodeModal && (
        <RegisterModal
          open={showLoginCodeModal}
          setOpen={setShowLoginCodeModal}
          onSuccessCallback={() => window.location.reload()}
        />
      )}
    </>
  );
}
interface EventTicketSelectorProps {
  eventTicketType: EventTicketType;
  getValues: UseFormGetValues<BuyTicketFormValues>;
  setValue: any;
  updateFormData: (
    updateFn: (
      prevData: BuyTicketFormValues | undefined
    ) => BuyTicketFormValues | undefined
  ) => void;
  event: Event;
  watch: UseFormWatch<BuyTicketFormValues>;
}
const EventTicketSelector = ({
  eventTicketType,
  getValues,
  setValue,
  updateFormData,
  event,
  watch,
}: EventTicketSelectorProps) => {
  const locale = useCurrentLocale();
  const t = useTranslations('Pages.Event.EventPageSelectTicketSection');
  const quantity =
    watch(`eventTicketTypes.${eventTicketType?.id}`)?.quantity || 0;
  const updateTicketQuantity = (newQuantity: number) => {
    setValue(`eventTicketTypes.${eventTicketType.id}`, {
      quantity: newQuantity,
    });
  };
  const watcher = watch(`eventTicketTypes.${eventTicketType?.id}`);
  useEffect(() => {
    const t = getValues().eventTicketTypes;
    if (!t?.[eventTicketType?.id]) return;
    t[eventTicketType?.id].quantity = watcher?.quantity;
    updateFormData(() => {
      return {
        ...getValues(),
        event,
        randomUUid: generateUUID(),
        eventTicketTypes: t,
      };
    });
  }, [watcher?.quantity]);

  const hasExceededMaxTicketPerPerson =
    EventTicketTypeHelper.hasExceededMaxTicketPerPerson(eventTicketType);

  const isWithdrawnFromSale = eventTicketType.maxTicketPerPerson === 0;

  const willExceededMaxTicketPerPerson =
    EventTicketTypeHelper.willExceedMaxTicketPerPerson(
      eventTicketType,
      (eventTicketType?.userTicketsBought || 0) + (quantity || 0)
    ) ||
    EventTicketTypeHelper.willExceedMaxTicket(eventTicketType, quantity || 0);

  const prices = useMemo(
    () =>
      DiscountEventTicketTypeHelper.getPriceAndMinDiscountPrice(
        eventTicketType
      ),
    [eventTicketType]
  );

  const discountWithMinimumPrice =
    DiscountEventTicketTypeHelper.getDiscountWithMinimumPrice(eventTicketType);

  const willExceedDiscountUsage =
    DiscountEventTicketTypeHelper.willExceedDiscountUsage(
      quantity || 0,
      discountWithMinimumPrice
    );

  const canRemoveMore = quantity > 0;
  const isSoldOut = EventTicketTypeHelper.isSoldOut(eventTicketType);

  useEffect(() => {
    if (isSoldOut || hasExceededMaxTicketPerPerson) {
      const t = getValues().eventTicketTypes;
      if (!t?.[eventTicketType?.id]) return;
      t[eventTicketType?.id].quantity = 0;
      updateFormData(() => {
        return {
          ...getValues(),
          event,
          randomUUid: generateUUID(),
          eventTicketTypes: t,
        };
      });
    }
  }, [isSoldOut, hasExceededMaxTicketPerPerson]);

  return (
    <div>
      {!!(
        eventTicketType.userTicketsBought &&
        eventTicketType.userTicketsBought > 0
      ) && (
        <p className='mb-1 text-xs font-semibold'>
          {t('numberTicketOfUser', {
            ticketsCount: eventTicketType.userTicketsBought,
          })}
        </p>
      )}
      {!!(
        eventTicketType.maxTicketPerPerson &&
        eventTicketType.maxTicketPerPerson > 0 &&
        !isWithdrawnFromSale
      ) && (
        <p className='mb-2 text-xs font-semibold'>
          {t('limitedTicketAmount', {
            maxTicketPerPerson: eventTicketType.maxTicketPerPerson,
          })}
        </p>
      )}
      {!!(
        eventTicketType?.ticketsLeft &&
        eventTicketType?.ticketsLeft > 0 &&
        eventTicketType?.ticketsLeft < 11
      ) && (
        <p className='mb-2 text-base font-bold'>
          {t('tenTicketsLeft', {
            ticketsCount: eventTicketType?.ticketsLeft,
          })}
        </p>
      )}
      <div
        className={clsx('rounded-md  p-4', {
          'bg-primary-filled-default':
            !isSoldOut && !hasExceededMaxTicketPerPerson,
          'bg-primary-faded-default':
            isSoldOut || hasExceededMaxTicketPerPerson,
        })}
      >
        <div>
          <p className='text-lg font-bold'>{eventTicketType.name}</p>
          {eventTicketType.description && (
            <RichText text={eventTicketType.description} />
          )}
        </div>
        {!isSoldOut && !hasExceededMaxTicketPerPerson && (
          <DiscountEventTicketTypeInfos
            discountEventTicketType={eventTicketType.discountEventTicketType}
            t={t}
          />
        )}
        {(hasExceededMaxTicketPerPerson || willExceededMaxTicketPerPerson) && (
          <p className='flex flex-row-reverse text-xs'>{t('cannotTakeMore')}</p>
        )}
        <div className='mt-2 flex items-center justify-between'>
          {prices.discountPrice !== undefined &&
          prices.discountPrice !== null ? (
            <div className='flex space-x-4'>
              <p className='text-xl font-bold text-gray-300 line-through'>
                {Format.price(prices.price || 0, locale)}
              </p>
              <p className='text-xl font-extrabold'>
                {Format.price(prices.discountPrice || 0, locale)}
              </p>
            </div>
          ) : (
            <p className='text-xl font-extrabold'>
              {Format.price(prices.price || 0, locale)}
            </p>
          )}
          {!(isSoldOut || hasExceededMaxTicketPerPerson) && (
            <NumberField
              defaultValue={0}
              disabled={
                isSoldOut ||
                hasExceededMaxTicketPerPerson ||
                willExceededMaxTicketPerPerson ||
                willExceedDiscountUsage ||
                isWithdrawnFromSale
              }
              variant='ticket'
              onChange={(e) => {
                updateTicketQuantity(e);
              }}
              color={{
                border: 'border-white-filled-default',
                text: 'text-white-filled-default',
              }}
            />
          )}
          {(isSoldOut ||
            hasExceededMaxTicketPerPerson ||
            isWithdrawnFromSale) && <p>{t('soldOut')}</p>}
        </div>
      </div>
    </div>
  );
};

interface DiscountEventTicketTypeInfosProps {
  discountEventTicketType?: DiscountEventTicketType[];
  t: any;
}

function DiscountEventTicketTypeInfos({
  discountEventTicketType,
  t,
}: DiscountEventTicketTypeInfosProps) {
  return (
    <div>
      {!!(discountEventTicketType && discountEventTicketType.length > 0) && (
        <div className='mt-2 w-full'>
          {discountEventTicketType.length > 1 && (
            <p className='mb-2 text-xs font-bold'>{t('multiplePromoCode')}</p>
          )}
          <div>
            {discountEventTicketType.map((dett) => (
              <DiscountEventTicketTypeInfo
                key={dett.id}
                discountEventTicketType={dett}
                t={t}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface DiscountEventTicketTypeInfoProps {
  discountEventTicketType: DiscountEventTicketType;
  t: any;
}

function DiscountEventTicketTypeInfo({
  discountEventTicketType,
  t,
}: DiscountEventTicketTypeInfoProps) {
  const hasExceedDiscountUsage =
    DiscountEventTicketTypeHelper.hasExceedDiscountUsage(
      discountEventTicketType
    );

  if (hasExceedDiscountUsage) {
    return (
      <p>
        {t('notApplicable1')}{' '}
        <span className='font-extrabold'>
          {discountEventTicketType.discount.name} {t('notApplicable2')}
        </span>
      </p>
    );
  }

  return (
    <Disclosure
      bg='l'
      body={
        <div className=''>
          <div className=''>
            <p className='text-xs font-normal'>
              {t('appliesDiscountOf')}{' '}
              {discountEventTicketType.type === 'flat'
                ? `${getPriceWithCurrency(discountEventTicketType.value / 100)}`
                : `${discountEventTicketType.value}%`}
              .
            </p>
            <p className='text-xs'>
              {discountEventTicketType?.usageLeft !== null &&
                discountEventTicketType?.usageLeft !== undefined && (
                  <>
                    {t('canBeUsedBy')}
                    <span className='font-normal'>
                      {' '}
                      {t('everybody')} {discountEventTicketType?.usageLeft || 0}{' '}
                      {t('times')}
                    </span>
                  </>
                )}
              {(discountEventTicketType as any).discount.userUsageLeft !==
                null &&
                (discountEventTicketType as any).discount.userUsageLeft !==
                  undefined && (
                  <p>
                    {' '}
                    {t('orBy')}{' '}
                    <span className='font-normal'>
                      {t('yourself')}{' '}
                      {(discountEventTicketType as any).discount.userUsageLeft}{' '}
                      {t('times')}
                    </span>
                    .
                  </p>
                )}
            </p>
          </div>
        </div>
      }
      title={
        <div className='flex items-center justify-between'>
          <div className='text-xs font-normal'>
            {t('applicable1')}{' '}
            <span className='font-normal'>
              {discountEventTicketType.discount.name} {t('applicable2')}
            </span>
          </div>
        </div>
      }
      id='event-details'
    />
  );
}
